import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { cn } from "@/lib/utils"
import {
  IconCalendarEvent,
  IconPlus,
  IconDots,
  IconPencil,
  IconTrash,
  IconPlayerPause,
  IconPlayerPlay,
  IconLock,
  IconSparkles,
  IconArrowRight,
} from "@tabler/icons-react"
import { format } from "date-fns"
import { Button } from "@/components/ui/button"
import { DataTable, type DataTableColumn } from "@/components/ui/data-table"
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalTitle,
  ModalDescription,
} from "@/components/Modal"
import { Input as CustomInput } from "@/components/Input"
import { Label } from "@/components/ui/label"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { ButtonSelect } from "@/components/ButtonSelect"
import { LuClock, LuCalendar, LuUsers, LuChevronDown, LuSearch, LuMinus, LuCheck } from "react-icons/lu"
import { Checkbox } from "@/components/ui/checkbox"
import { toast } from "sonner"
import { Chip } from "@/components/Chip"
import { ListTooltip, type ListTooltipItem } from "@/components/ListTooltip"
import { ModelsSelector, ALL_MODELS_SELECTION } from "@/components/ModelsSelector"
import type { ModelSelection } from "@/data/types/common.types"
import { InlineMetadataFilters, type AppliedFilter } from "@/components/FiltersSelector"
import {
  useCreateScheduledReport,
  useUpdateScheduledReport,
  usePauseScheduledReport,
  useResumeScheduledReport,
  useDeleteScheduledReport,
  useModels,
} from "@/data/hooks/api"
import type {
  ScheduledReport,
  TeamMember,
  ScheduleFrequency,
  DailyScheduleType,
  WeekDay,
  MonthlyWeek,
  MonthlyDay,
  ReportPeriod,
  ScheduleConfig,
  CreateScheduledReportRequest,
  UpdateScheduledReportRequest,
  ReportPayload,
  MetadataFilter,
} from "./types"
import {
  FREQUENCY_OPTIONS,
  DAILY_SCHEDULE_OPTIONS,
  WEEKDAY_OPTIONS,
  MONTHLY_SCHEDULE_TYPE_OPTIONS,
  MONTHLY_DAY_OPTIONS,
  MONTHLY_WEEK_OPTIONS,
  MONTHLY_DAY_NAME_OPTIONS,
  REPORT_PERIOD_OPTIONS,
  TIME_OPTIONS,
  TIMEZONE_OPTIONS,
  getUserTimezone,
} from "./constants"

// Recipients Selector Component
interface RecipientsSelectorProps {
  value: string[]
  onChange: (userIds: string[]) => void
  members: TeamMember[]
  loading: boolean
}

function RecipientsSelector({ value, onChange, members, loading }: RecipientsSelectorProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  const selectedMembers = members.filter((m) => value.includes(m.id))
  const selectedCount = selectedMembers.length

  const filteredMembers = searchQuery
    ? members.filter(
      (m) =>
        m.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.email.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : members

  const isAllSelected = members.length > 0 && selectedCount === members.length
  const isNoneSelected = selectedCount === 0

  const handleToggleMember = (userId: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (!userId) return // Prevent adding null/undefined IDs

    if (value.includes(userId)) {
      onChange(value.filter((id) => id !== userId))
    } else {
      onChange([...value, userId])
    }
  }

  const handleSelectAll = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    onChange(members.map((m) => m.id))
  }

  const handleClearAll = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    onChange([])
  }

  const getDisplayText = () => {
    if (loading) return "Loading..."
    if (isAllSelected) return "All Recipients"
    if (isNoneSelected) return "No Recipients"
    return `${selectedCount} Recipient${selectedCount === 1 ? "" : "s"}`
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="transparent-outline"
          size="xs"
          disabled={loading}
          className={cn(
            "hover:bg-[#F2EEFF] hover:text-[#654EDA] hover:border-[#E2DAFF]",
            "focus-visible:ring-[#654EDA]/20 focus-visible:border-[#654EDA] focus-visible:ring-2",
            open && "bg-[#E2DAFF] text-[#654EDA] border-[#C6B9FF] hover:bg-[#E2DAFF] focus-visible:ring-0"
          )}
        >
          <LuUsers className="size-4" />
          {getDisplayText()}
          <LuChevronDown
            className={cn(
              "size-3.5 ml-0.5 transition-transform duration-200",
              open && "rotate-180"
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[320px] p-0 border-[#EDEEF3] shadow-lg flex flex-col max-h-[var(--radix-popover-content-available-height,400px)]"
        align="start"
        sideOffset={6}
      >
        <div className="p-3 border-b border-[#EDEEF3] shrink-0">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#8A8F98]" />
              <input
                type="text"
                placeholder="Search recipients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-[30px] pl-9 pr-3 rounded-[6px] border border-[#EDEEF3] bg-white text-[13px] text-[#08090A] placeholder:text-[#8A8F98] hover:border-[#D1D5DB] focus:outline-none focus:border-[#654EDA] transition-colors"
              />
            </div>
            {!loading && selectedCount > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                className="text-[12px] text-[#8A8F98] hover:text-[#654EDA] transition-colors whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="p-4 text-center text-[13px] text-[#8A8F98]">
              Loading recipients...
            </div>
          ) : members.length === 0 ? (
            <div className="p-4 text-center text-[13px] text-[#8A8F98]">
              No team members available
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="p-4 text-center text-[13px] text-[#8A8F98]">
              No recipients found
            </div>
          ) : (
            <>
              {!searchQuery && (
                <div
                  className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#F7F8F8] transition-colors border-b border-[#EDEEF3]"
                  onClick={handleSelectAll}
                >
                  <div className="relative">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={() => handleSelectAll()}
                      className={cn(
                        "size-4 border-[#D1D5DB] data-[state=checked]:bg-[#654EDA] data-[state=checked]:border-[#654EDA]",
                        !isAllSelected && !isNoneSelected && "data-[state=unchecked]:bg-[#654EDA]/20 data-[state=unchecked]:border-[#654EDA]"
                      )}
                    />
                    {!isAllSelected && !isNoneSelected && (
                      <LuMinus className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-3 text-[#654EDA]" />
                    )}
                  </div>
                  <LuUsers className="size-4 text-[#8A8F98]" />
                  <span className="text-[13px] font-medium text-[#08090A]">
                    Select All
                  </span>
                  <span className="ml-auto text-[12px] text-[#8A8F98]">
                    {selectedCount}/{members.length}
                  </span>
                </div>
              )}

              {filteredMembers.map((member) => {
                const isSelected = value.includes(member.id)
                return (
                  <div
                    key={member.id}
                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#F7F8F8] transition-colors border-b border-[#EDEEF3] last:border-b-0"
                    onClick={(e) => handleToggleMember(member.id, e)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleToggleMember(member.id)}
                      className="size-4 border-[#D1D5DB] data-[state=checked]:bg-[#654EDA] data-[state=checked]:border-[#654EDA]"
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-[13px] text-[#08090A] truncate">
                        {member.first_name} {member.last_name}
                      </span>
                      <span className="text-[11px] text-[#8A8F98] truncate">
                        {member.email}
                      </span>
                    </div>
                    {isSelected && (
                      <LuCheck className="size-4 text-[#654EDA] shrink-0" />
                    )}
                  </div>
                )
              })}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// Weekly Day Selector Component
interface WeekdaySelectorProps {
  value: WeekDay[]
  onChange: (days: WeekDay[]) => void
}

function WeekdaySelector({ value, onChange }: WeekdaySelectorProps) {
  const toggleDay = (day: WeekDay) => {
    if (value.includes(day)) {
      // Don't allow deselecting if it's the last one
      if (value.length > 1) {
        onChange(value.filter((d) => d !== day))
      }
    } else {
      onChange([...value, day])
    }
  }

  return (
    <div className="flex gap-[6px]">
      {WEEKDAY_OPTIONS.map((day) => {
        const isSelected = value.includes(day.value)
        return (
          <button
            key={day.value}
            type="button"
            onClick={() => toggleDay(day.value)}
            className={cn(
              "flex items-center justify-center size-[32px] rounded-[6px] text-[12px] font-medium transition-all",
              "border border-[#EDEEF3] hover:border-[#D1D5DB]",
              isSelected && "bg-[#654EDA] text-white border-[#654EDA] hover:border-[#654EDA] hover:bg-[#654EDA]/90"
            )}
            title={day.label}
          >
            {day.shortLabel}
          </button>
        )
      })}
    </div>
  )
}

// Helper to get frequency display from the API response
const getFrequencyDisplayLabel = (report: ScheduledReport) => {
  // Use the pre-computed display from API if available
  if (report.frequency_display) {
    return report.frequency_display
  }

  // Fallback to computing it ourselves
  const config = report.schedule_config
  switch (config.frequency) {
    case "daily":
      if (config.type === "weekdays_only") {
        return "Weekdays"
      }
      return "Daily"
    case "weekly":
      if (config.days && config.days.length > 0) {
        const days = config.days
          .map((d) => WEEKDAY_OPTIONS.find((w) => w.value === d)?.shortLabel || d)
          .join(", ")
        return `Weekly (${days})`
      }
      return "Weekly"
    case "monthly":
      if (config.type === "specific_date" && config.day_of_month) {
        return `Monthly (${config.day_of_month}${getOrdinalSuffix(config.day_of_month)})`
      }
      if (config.type === "relative_day") {
        const week = MONTHLY_WEEK_OPTIONS.find((w) => w.value === config.week)?.label || ""
        const day = MONTHLY_DAY_NAME_OPTIONS.find((d) => d.value === config.day)?.label || ""
        return `Monthly (${week} ${day})`
      }
      return "Monthly"
    default:
      return "Unknown"
  }
}

function getOrdinalSuffix(n: number): string {
  const s = ["th", "st", "nd", "rd"]
  const v = n % 100
  return s[(v - 20) % 10] || s[v] || s[0]
}

// Helper to get time label from hour number
const getTimeLabel = (hour: number): string => {
  const option = TIME_OPTIONS.find((t) => t.value === hour)
  return option?.label || `${hour}:00`
}

// Form state types for monthly configuration
type MonthlyFormType = "specific_day" | "relative_day"

interface ScheduledReportsProps {
  reports: ScheduledReport[]
  total: number
  pageIndex: number
  pageSize: number
  onPageChange: (pageIndex: number) => void
  onPageSizeChange: (pageSize: number) => void
  teamMembers: TeamMember[]
  loadingMembers: boolean
  loading: boolean
  onScheduleChanged?: () => void
  isOnFreePlan?: boolean
}

export function ScheduledReports({
  reports,
  total,
  pageIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
  teamMembers,
  loadingMembers,
  loading,
  onScheduleChanged,
  isOnFreePlan = false,
}: ScheduledReportsProps) {
  // Create/Edit dialog state
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false)
  const [editingReport, setEditingReport] = useState<ScheduledReport | null>(null)

  // API mutations
  const createMutation = useCreateScheduledReport()
  const updateMutation = useUpdateScheduledReport()
  const pauseMutation = usePauseScheduledReport()
  const resumeMutation = useResumeScheduledReport()
  const deleteMutation = useDeleteScheduledReport()

  // Fetch models for default selection
  const { data: modelGroups } = useModels()
  const allModels = modelGroups ? modelGroups.flatMap((g) => g.models) : []

  // Form state
  const [formName, setFormName] = useState("")
  const [formFrequency, setFormFrequency] = useState<ScheduleFrequency>("daily")
  const [formDailyType, setFormDailyType] = useState<DailyScheduleType>("every_day")
  const [formWeeklyDays, setFormWeeklyDays] = useState<WeekDay[]>(["mon"])
  const [formMonthlyType, setFormMonthlyType] = useState<MonthlyFormType>("specific_day")
  const [formMonthlyDay, setFormMonthlyDay] = useState<number>(1)
  const [formRelativeWeek, setFormRelativeWeek] = useState<MonthlyWeek>("first")
  const [formRelativeDay, setFormRelativeDay] = useState<MonthlyDay>("monday")
  const [formReportPeriod, setFormReportPeriod] = useState<ReportPeriod>("last_7_days")
  const [formTimeHour, setFormTimeHour] = useState<number>(9)
  const [formTimezone, setFormTimezone] = useState<string>(getUserTimezone())
  const [formModels, setFormModels] = useState<ModelSelection>(ALL_MODELS_SELECTION)
  const [formMetadataFilters, setFormMetadataFilters] = useState<AppliedFilter[]>([])
  const [formRecipients, setFormRecipients] = useState<string[]>([])
  const [formIsEnabled, setFormIsEnabled] = useState(true)

  // Delete dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [deletingReport, setDeletingReport] = useState<ScheduledReport | null>(null)

  const isSaving = createMutation.isPending || updateMutation.isPending
  const isDeleting = deleteMutation.isPending

  // Auto-select all models when dialog opens for new report (not when editing)
  useEffect(() => {
    if (isFormDialogOpen && !editingReport && allModels.length > 0 && !formModels.all && formModels.providers.length === 0 && formModels.models.length === 0) {
      setFormModels(ALL_MODELS_SELECTION)
    }
  }, [isFormDialogOpen, allModels.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setFormName("")
    setFormFrequency("daily")
    setFormDailyType("every_day")
    setFormWeeklyDays(["mon"])
    setFormMonthlyType("specific_day")
    setFormMonthlyDay(1)
    setFormRelativeWeek("first")
    setFormRelativeDay("monday")
    setFormReportPeriod("last_7_days")
    setFormTimeHour(9)
    setFormTimezone(getUserTimezone())
    setFormModels(ALL_MODELS_SELECTION) // Reset to all models selected
    setFormMetadataFilters([])
    setFormRecipients([])
    setFormIsEnabled(true)
    setEditingReport(null)
  }

  const handleOpenCreateDialog = () => {
    resetForm()
    setIsFormDialogOpen(true)
  }

  const handleOpenEditDialog = (report: ScheduledReport) => {
    setEditingReport(report)
    setFormName(report.name)

    // Parse schedule config
    const config = report.schedule_config
    setFormFrequency(config.frequency)

    if (config.frequency === "daily") {
      setFormDailyType(config.type)
    } else if (config.frequency === "weekly") {
      setFormWeeklyDays(config.days)
    } else if (config.frequency === "monthly") {
      if (config.type === "specific_date") {
        setFormMonthlyType("specific_day")
        setFormMonthlyDay(config.day_of_month)
      } else {
        setFormMonthlyType("relative_day")
        setFormRelativeWeek(config.week)
        setFormRelativeDay(config.day)
      }
    }

    setFormReportPeriod(report.report_period)
    setFormTimeHour(report.time_hour)
    setFormTimezone(report.timezone)
    setFormModels(report.payload.model_selection ?? ALL_MODELS_SELECTION)
    setFormMetadataFilters(
      (report.payload.metadata_filters ?? []).map((f) => ({
        id: crypto.randomUUID(),
        tagName: f.key,
        tagNameLabel: f.key,
        operation: f.operator === "eq" ? "is" : "is_not",
        operationLabel: f.operator === "eq" ? "is" : "is not",
        value: f.value,
      }))
    )
    setFormRecipients(report.recipients.map((r) => r.user_id))
    setFormIsEnabled(report.is_enabled)
    setIsFormDialogOpen(true)
  }

  const handleCloseFormDialog = () => {
    setIsFormDialogOpen(false)
    resetForm()
  }

  // Build schedule config from form state
  const buildScheduleConfig = (): ScheduleConfig => {
    switch (formFrequency) {
      case "daily":
        return {
          frequency: "daily",
          type: formDailyType,
        }
      case "weekly":
        return {
          frequency: "weekly",
          days: formWeeklyDays,
        }
      case "monthly":
        if (formMonthlyType === "specific_day") {
          return {
            frequency: "monthly",
            type: "specific_date",
            day_of_month: formMonthlyDay,
          }
        } else {
          return {
            frequency: "monthly",
            type: "relative_day",
            week: formRelativeWeek,
            day: formRelativeDay,
          }
        }
    }
  }

  // Build payload from form state
  const buildPayload = (): ReportPayload => {
    const metadataFilters: MetadataFilter[] | null = formMetadataFilters.length > 0
      ? formMetadataFilters.map((f) => ({
        key: f.tagName,
        operator: f.operation === "is" ? "eq" as const : "neq" as const,
        value: f.value,
      }))
      : null

    return {
      report_type: "transactions",
      model_selection: formModels,
      providers: null,
      metadata_filters: metadataFilters,
    }
  }

  const handleSaveReport = async () => {
    if (!formName.trim()) {
      toast.error("Report name is required")
      return
    }

    if (formRecipients.length === 0) {
      toast.error("Please select at least one recipient")
      return
    }

    const scheduleConfig = buildScheduleConfig()
    const payload = buildPayload()

    try {
      if (editingReport) {
        // Update existing
        const updateData: UpdateScheduledReportRequest = {
          name: formName,
          report_period: formReportPeriod,
          schedule_config: scheduleConfig,
          time_hour: formTimeHour,
          timezone: formTimezone,
          payload,
          recipient_user_ids: formRecipients,
        }

        await updateMutation.mutateAsync({
          scheduleId: editingReport.id,
          data: updateData,
        })

        // Handle enable/disable state if changed
        if (editingReport.is_enabled !== formIsEnabled) {
          if (formIsEnabled) {
            await resumeMutation.mutateAsync(editingReport.id)
          } else {
            await pauseMutation.mutateAsync(editingReport.id)
          }
        }

        toast.success("Scheduled report updated successfully")
      } else {
        // Create new
        const createData: CreateScheduledReportRequest = {
          name: formName,
          report_period: formReportPeriod,
          schedule_config: scheduleConfig,
          time_hour: formTimeHour,
          timezone: formTimezone,
          payload,
          recipient_user_ids: formRecipients,
        }

        await createMutation.mutateAsync(createData)
        toast.success("Scheduled report created successfully")
      }

      handleCloseFormDialog()
      onScheduleChanged?.()
    } catch (error) {
      toast.error(editingReport ? "Failed to update schedule" : "Failed to create schedule")
      console.error("Failed to save scheduled report:", error)
    }
  }

  const handleToggleActive = async (report: ScheduledReport) => {
    try {
      if (report.is_enabled) {
        await pauseMutation.mutateAsync(report.id)
        toast.success("Scheduled report paused")
      } else {
        await resumeMutation.mutateAsync(report.id)
        toast.success("Scheduled report activated")
      }
      onScheduleChanged?.()
    } catch (error) {
      toast.error("Failed to update schedule status")
      console.error("Failed to toggle schedule active state:", error)
    }
  }

  const handleOpenDeleteDialog = (report: ScheduledReport) => {
    setDeletingReport(report)
    setIsDeleteDialogOpen(true)
  }

  const handleDeleteReport = async () => {
    if (!deletingReport) return

    try {
      await deleteMutation.mutateAsync(deletingReport.id)
      setIsDeleteDialogOpen(false)
      setDeletingReport(null)
      toast.success("Scheduled report deleted successfully")
      onScheduleChanged?.()
    } catch (error) {
      toast.error("Failed to delete schedule")
      console.error("Failed to delete scheduled report:", error)
    }
  }

  // Column definitions
  const columns: DataTableColumn<ScheduledReport>[] = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      flex: 1.5,
      cell: ({ row }) => (
        <span className="font-normal text-[#08090A]">{row.original.name}</span>
      ),
    },
    {
      id: "frequency",
      header: "Frequency",
      accessorKey: "schedule_config",
      flex: 1.1,
      cell: ({ row }) => {
        const report = row.original
        const timeLabel = getTimeLabel(report.time_hour)
        return (
          <div className="flex flex-col gap-[2px]">
            <span className="text-[#08090A]">{getFrequencyDisplayLabel(report)}</span>
            <span className="text-[11px] text-[#8A8F98]">at {timeLabel}</span>
          </div>
        )
      },
    },
    {
      id: "next_run",
      header: "Next Run",
      accessorKey: "next_run_at",
      flex: 1,
      cell: ({ row }) => {
        const report = row.original
        if (!report.is_enabled || !report.next_run_at) {
          return <span className="text-[#8A8F98]">—</span>
        }
        // Format timezone for display (e.g., "America/New_York" -> "New York")
        const tzAbbrev = report.timezone?.split("/").pop()?.replace(/_/g, " ") || report.timezone
        return (
          <div className="flex flex-col gap-[2px]">
            <span className="text-[#8A8F98]">{format(new Date(report.next_run_at), "MMM d, yyyy HH:mm")}</span>
            <span className="text-[11px] text-[#8A8F98]/70">{tzAbbrev}</span>
          </div>
        )
      },
    },
    {
      id: "last_run",
      header: "Last Run",
      accessorKey: "last_run_at",
      flex: 0.8,
      cell: ({ row }) => (
        <span className="text-[#8A8F98]">
          {row.original.last_run_at ? format(new Date(row.original.last_run_at), "MMM d, yyyy HH:mm") : "Never"}
        </span>
      ),
    },
    {
      id: "recipients",
      header: "Recipients",
      flex: 0.8,
      cell: ({ row }) => {
        const recipients = row.original.recipients
        const tooltipItems: ListTooltipItem[] = recipients.map((r) => ({
          id: r.user_id,
          label: `${r.first_name} ${r.last_name}`,
        }))

        return (
          <ListTooltip items={tooltipItems} emptyMessage="No recipients" side="bottom" align="start">
            <div className="cursor-default">
              <Chip
                label={`Recipients (${recipients.length})`}
                icon={<LuUsers className="size-3 text-[#8A8F98]" />}
              />
            </div>
          </ListTooltip>
        )
      },
    },
    {
      id: "status",
      header: "Status",
      flex: 0.6,
      cell: ({ row }) => {
        const isEnabled = row.original.is_enabled
        return (
          <Chip label={isEnabled ? "Active" : "Paused"} showDot dotColor={isEnabled ? "#2ECC71" : "#34495E"} />
        )
      },
    },
    {
      id: "actions",
      header: "",
      flex: 0.4,
      enableSorting: false,
      cell: ({ row }) => {
        const report = row.original
        const isToggling = pauseMutation.isPending || resumeMutation.isPending
        return (
          <div className="flex items-center justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center justify-center size-[28px] rounded-[4px] text-[#8A8F98] hover:text-[#08090A] hover:bg-[#EDEEF3] transition-colors cursor-pointer">
                  <IconDots className="size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="min-w-[160px] p-[4px] rounded-[6px] border-[#EDEEF3] shadow-lg"
              >
                <DropdownMenuItem
                  onClick={() => handleOpenEditDialog(report)}
                  className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#08090A] cursor-pointer"
                >
                  <IconPencil className="mr-[8px] size-3.5 text-[#8A8F98]" />
                  Edit Schedule
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleToggleActive(report)}
                  disabled={isToggling}
                  className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#08090A] cursor-pointer"
                >
                  {report.is_enabled ? (
                    <>
                      <IconPlayerPause className="mr-[8px] size-3.5 text-[#8A8F98]" />
                      Pause Schedule
                    </>
                  ) : (
                    <>
                      <IconPlayerPlay className="mr-[8px] size-3.5 text-[#8A8F98]" />
                      Activate Schedule
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="my-[4px] bg-[#EDEEF3]" />
                <DropdownMenuItem
                  onClick={() => handleOpenDeleteDialog(report)}
                  className="text-[13px] h-[32px] px-[8px] rounded-[4px] text-[#E74C3C] cursor-pointer focus:text-[#E74C3C] focus:bg-[#E74C3C]/5"
                >
                  <IconTrash className="mr-[8px] size-3.5" />
                  Delete Schedule
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )
      },
    },
  ]

  // Show only upgrade promo for free plan users
  if (isOnFreePlan) {
    return (
      <div className="space-y-[24px]">
        <h2 className="text-[18px] font-medium text-[#08090A] px-[4px]">Scheduled Reports</h2>
        <div className="relative overflow-hidden rounded-[12px] border border-[#654EDA]/20 bg-gradient-to-br from-[#654EDA]/5 via-white to-[#9B59B6]/5 p-[24px]">
          <div className="flex items-center gap-[8px] mb-[8px]">
            <IconLock className="size-4 text-[#654EDA]" />
            <span className="text-[12px] font-medium text-[#654EDA] uppercase tracking-wide">Starter Plan Feature</span>
          </div>
          <h3 className="text-[18px] font-medium text-[#08090A] mb-[8px]">
            Automate your cost reporting
          </h3>
          <p className="text-[13px] text-[#8A8F98] leading-[20px] mb-[16px]">
            Schedule reports to be delivered automatically to your team. Set up daily, weekly, or monthly
            summaries and never miss critical spending insights again.
          </p>
          <Link to="/organization/billing">
            <Button
              size="sm"
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium px-[16px] rounded-[6px] shadow-[0_2px_8px_rgba(101,78,218,0.2)]"
            >
              <IconSparkles className="size-3.5 mr-[6px]" />
              Upgrade to Starter
              <IconArrowRight className="size-3.5 ml-[6px]" />
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-[24px]">
        <div className="space-y-[13px]">
          <div className="flex items-center justify-between">
            <h2 className="text-[18px] font-medium text-[#08090A] px-[4px]">All Schedules</h2>
            <Button
              onClick={handleOpenCreateDialog}
              size="sm"
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium px-[13px] rounded-[6px]"
            >
              <IconPlus className="size-4" />
              Schedule Report
            </Button>
          </div>
          <DataTable
            data={reports}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id}
            emptyMessage="No scheduled reports yet"
            emptyIcon={<IconCalendarEvent className="size-6 text-[#8A8F98]" />}
            pagination={{
              enabled: true,
              mode: "server",
              pageIndex,
              pageSize,
              totalItems: total,
              onPageChange,
              onPageSizeChange,
              pageSizeOptions: [5, 10, 20],
            }}
          />
        </div>
      </div>

      {/* Create/Edit Scheduled Report Modal */}
      <Modal open={isFormDialogOpen} onOpenChange={handleCloseFormDialog}>
        <ModalContent className="max-w-[520px] max-h-[90vh] overflow-y-auto p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[5px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">
              {editingReport ? "Edit Scheduled Report" : "Schedule Report"}
            </ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Configure the schedule, filters, and recipients for your recurring report.
            </ModalDescription>
          </ModalHeader>

          <div className="px-[24px] py-[24px] space-y-[16px]">
            {/* Name */}
            <div className="space-y-[6px]">
              <Label htmlFor="schedule-name" className="text-[12px] text-[#08090A]">
                Report Name *
              </Label>
              <CustomInput
                id="schedule-name"
                size="sm"
                placeholder="e.g., Weekly Cost Summary"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                disabled={isSaving}
                className="w-full"
              />
            </div>

            {/* Report Period */}
            <div className="space-y-[6px]">
              <Label className="text-[12px] text-[#08090A]">Report Period *</Label>
              <ButtonSelect
                value={formReportPeriod}
                onValueChange={(v) => setFormReportPeriod(v as ReportPeriod)}
                options={REPORT_PERIOD_OPTIONS}
                icon={<LuCalendar className="size-4" />}
                header="Report Period"
                dropdownWidth={180}
                size="sm"
                className="w-[calc(50%-8px)] justify-between"
                dropdownClassName="max-h-[240px]"
              />
            </div>

            {/* Schedule Section */}
            <div className="space-y-[6px]">
              <Label className="text-[12px] text-[#08090A]">Schedule *</Label>
              <div className="rounded-[8px] border border-[#EDEEF3] overflow-hidden">
                {/* Frequency Segmented Control */}
                <div className="flex h-[32px] bg-[#F7F8F8]">
                  {FREQUENCY_OPTIONS.map((option, index) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormFrequency(option.value)}
                      className={cn(
                        "cursor-pointer flex-1 h-full text-[13px] font-medium transition-all duration-150",
                        index !== FREQUENCY_OPTIONS.length - 1 && "border-r border-[#EDEEF3]",
                        formFrequency === option.value
                          ? "bg-white text-[#654EDA]"
                          : "text-[#8A8F98] hover:text-[#08090A] hover:bg-[#EDEEF3]/50"
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                {/* Schedule Configuration */}
                <div className="p-[12px] bg-white border-t border-[#EDEEF3]">
                  {/* Daily Options */}
                  {formFrequency === "daily" && (
                    <div className="space-y-[6px]">
                      <Label className="text-[12px] text-[#8A8F98]">Schedule Type</Label>
                      <ButtonSelect
                        value={formDailyType}
                        onValueChange={(v) => setFormDailyType(v as DailyScheduleType)}
                        options={DAILY_SCHEDULE_OPTIONS}
                        header="Schedule Type"
                        dropdownWidth={180}
                        size="sm"
                        className="w-full justify-between"
                        dropdownClassName="max-h-[240px]"
                      />
                    </div>
                  )}

                  {/* Weekly Options */}
                  {formFrequency === "weekly" && (
                    <div className="space-y-[6px]">
                      <Label className="text-[12px] text-[#8A8F98]">Days of the Week</Label>
                      <WeekdaySelector value={formWeeklyDays} onChange={setFormWeeklyDays} />
                    </div>
                  )}

                  {/* Monthly Options */}
                  {formFrequency === "monthly" && (
                    <div className="space-y-[12px]">
                      <div className="space-y-[6px]">
                        <Label className="text-[12px] text-[#8A8F98]">Schedule Type</Label>
                        <ButtonSelect
                          value={formMonthlyType}
                          onValueChange={(v) => setFormMonthlyType(v as MonthlyFormType)}
                          options={MONTHLY_SCHEDULE_TYPE_OPTIONS}
                          header="Schedule Type"
                          dropdownWidth={200}
                          size="sm"
                          className="w-full justify-between"
                          dropdownClassName="max-h-[240px]"
                        />
                      </div>

                      {formMonthlyType === "specific_day" ? (
                        <div className="space-y-[6px]">
                          <Label className="text-[12px] text-[#8A8F98]">Day of Month</Label>
                          <ButtonSelect
                            value={String(formMonthlyDay)}
                            onValueChange={(v) => setFormMonthlyDay(Number(v))}
                            options={MONTHLY_DAY_OPTIONS.map((opt) => ({ value: String(opt.value), label: opt.label }))}
                            header="Day of Month"
                            dropdownWidth={120}
                            size="sm"
                            className="w-[120px] justify-between"
                            dropdownClassName="max-h-[240px]"
                          />
                        </div>
                      ) : (
                        <div className="flex gap-[12px]">
                          <div className="space-y-[6px] flex-1">
                            <Label className="text-[12px] text-[#8A8F98]">Week</Label>
                            <ButtonSelect
                              value={formRelativeWeek}
                              onValueChange={(v) => setFormRelativeWeek(v as MonthlyWeek)}
                              options={MONTHLY_WEEK_OPTIONS}
                              header="Week"
                              dropdownWidth={140}
                              size="sm"
                              className="w-full justify-between"
                              dropdownClassName="max-h-[240px]"
                            />
                          </div>
                          <div className="space-y-[6px] flex-1">
                            <Label className="text-[12px] text-[#8A8F98]">Day</Label>
                            <ButtonSelect
                              value={formRelativeDay}
                              onValueChange={(v) => setFormRelativeDay(v as MonthlyDay)}
                              options={MONTHLY_DAY_NAME_OPTIONS}
                              header="Day"
                              dropdownWidth={160}
                              size="sm"
                              className="w-full justify-between"
                              dropdownClassName="max-h-[240px]"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Time and Timezone */}
            <div className="grid grid-cols-2 gap-[16px]">
              {/* Time of Day */}
              <div className="space-y-[6px]">
                <Label className="text-[12px] text-[#08090A]">Time *</Label>
                <ButtonSelect
                  value={String(formTimeHour)}
                  onValueChange={(v) => setFormTimeHour(Number(v))}
                  options={TIME_OPTIONS.map((t) => ({ value: String(t.value), label: t.label }))}
                  icon={<LuClock className="size-4" />}
                  header="Time"
                  dropdownWidth={140}
                  size="sm"
                  className="w-full justify-between"
                  dropdownClassName="max-h-[240px]"
                />
              </div>

              {/* Timezone */}
              <div className="space-y-[6px]">
                <Label className="text-[12px] text-[#08090A]">Timezone *</Label>
                <ButtonSelect
                  value={formTimezone}
                  onValueChange={(v) => setFormTimezone(v)}
                  options={TIMEZONE_OPTIONS}
                  header="Timezone"
                  dropdownWidth={220}
                  size="sm"
                  className="w-full justify-between"
                  dropdownClassName="max-h-[240px]"
                />
              </div>
            </div>

            {/* Models Filter */}
            <div className="space-y-[6px]">
              <Label className="text-[12px] text-[#08090A]">Filter by Models</Label>
              <div>
                <ModelsSelector value={formModels} onChange={setFormModels} />
              </div>
            </div>

            {/* Metadata Filters */}
            <div className="space-y-[6px]">
              <Label className="text-[12px] text-[#08090A]">Filters</Label>
              <InlineMetadataFilters
                value={formMetadataFilters}
                onChange={setFormMetadataFilters}
                addButtonLabel="Add Filter"
              />
            </div>

            {/* Recipients */}
            <div className="space-y-[6px]">
              <Label className="text-[12px] text-[#08090A]">Recipients *</Label>
              <RecipientsSelector
                value={formRecipients}
                onChange={setFormRecipients}
                members={teamMembers}
                loading={loadingMembers}
              />
            </div>

            {/* Active State */}
            <div className="flex items-center space-x-[8px] pt-[4px]">
              <Checkbox
                id="is-enabled"
                checked={formIsEnabled}
                onCheckedChange={(checked) => setFormIsEnabled(checked === true)}
                disabled={isSaving}
                className="border-[#EDEEF3]"
              />
              <Label htmlFor="is-enabled" className="text-[12px] text-[#08090A] cursor-pointer">
                Enable this schedule immediately
              </Label>
            </div>
          </div>

          <ModalFooter className="justify-end p-[24px] pt-0 gap-[8px]">
            <Button
              variant="transparent-outline"
              onClick={handleCloseFormDialog}
              disabled={isSaving}
              size="sm"
              className="h-[32px] font-medium border-[#EDEEF3]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveReport}
              disabled={isSaving}
              size="sm"
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
            >
              {isSaving
                ? editingReport
                  ? "Updating..."
                  : "Creating..."
                : editingReport
                  ? "Update Schedule"
                  : "Create Schedule"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Modal */}
      <Modal open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <ModalContent className="max-w-[480px] p-0 border-[#EDEEF3] shadow-2xl">
          <ModalHeader className="p-[24px] pb-0 space-y-[13px]">
            <ModalTitle className="text-[20px] font-medium text-[#08090A]">Delete Scheduled Report</ModalTitle>
            <ModalDescription className="text-[13px] leading-[22px] text-[#8A8F98]">
              Are you sure you want to delete the scheduled report{" "}
              <span className="font-semibold text-[#08090A]">{deletingReport?.name}</span>? This action cannot be
              undone.
            </ModalDescription>
          </ModalHeader>

          <ModalFooter className="justify-end p-[24px] gap-[8px]">
            <Button
              variant="transparent-outline"
              size="sm"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
              className="h-[32px] font-medium border-[#EDEEF3]"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleDeleteReport}
              disabled={isDeleting}
              size="sm"
              className="h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium"
            >
              {isDeleting ? "Deleting..." : "Delete Schedule"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
